import { createFileRoute } from '@tanstack/react-router'
import { useVouchers, useEvent, useVoucherCounts, useCreateVoucher, useBulkImportVouchers, useMenuItems, useDeleteVoucher, useAssignVoucher, useEventUsers } from '@/lib/api/queries'
import { useState, useRef, useEffect } from 'react'
import { formatDate } from '@/lib/date'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { X, Upload, Plus, Eye, ChevronLeft, ChevronRight, Search, Gift, FileText, UserPlus } from 'lucide-react'
import type { MenuItem, Voucher, User } from '@/lib/api/types'
import { AccessDenied } from '@/components/permission-guard'
import { usePermission } from '@/lib/permissions'

export const Route = createFileRoute('/_authenticated/events/$eventId/vouchers')({
  component: VouchersPage,
})

type VoucherStatus = 'all' | 'claimed' | 'unclaimed' | 'expired'
type VoucherType = 'token' | 'items'

// Helper to compute voucher display status based on times_claimed and expiry
function getVoucherDisplayStatus(voucher: Voucher): 'claimed' | 'unclaimed' | 'expired' {
  // Check if expired first
  if (voucher.expiry) {
    const expiryDate = new Date(voucher.expiry)
    if (expiryDate < new Date()) {
      return 'expired'
    }
  }
  // Then check if claimed
  if ((voucher.times_claimed || 0) > 0) {
    return 'claimed'
  }
  return 'unclaimed'
}

// The API doesn't send remaining_uses or unique_users yet, so derive them from
// times_claimed until it does
function getRemainingUses(voucher: Voucher, maxUses: number): number {
  return voucher.remaining_uses ?? Math.max(maxUses - (voucher.times_claimed || 0), 0)
}

function getUniqueUsersDisplay(voucher: Voucher): number | string {
  if (voucher.unique_users != null) {
    return voucher.unique_users
  }
  // One claim per user means every claim is a different user
  if ((voucher.max_uses_per_user || 1) === 1) {
    return voucher.times_claimed || 0
  }
  return '—'
}

interface ItemGroup {
  quantity: number
  items: number[]
  searchQuery: string
}

interface CreateVoucherData {
  name: string
  type: VoucherType
  value: number
  max_uses: number | ''
  max_uses_per_user: number | ''
  expiry_date: string
  expiry_time: string
  is_available: boolean
  hide_voucher_name: boolean
}

function VouchersPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  
  // Permission checks
  const canViewVouchers = usePermission('can_view_vouchers')
  const canEditVouchers = usePermission('can_edit_vouchers')
  const canBulkImportVouchers = usePermission('can_bulk_import_vouchers')
  const canDeleteVouchers = usePermission('can_delete_vouchers')
  void canDeleteVouchers // Future use
  
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<VoucherStatus>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null)
  const pageSize = 20
  
  const { data: event } = useEvent(eventIdNum)
  void event // Future use
  
  // If user doesn't have permission to view vouchers, show access denied
  if (!canViewVouchers) {
    return <AccessDenied message="You don't have permission to view vouchers." />
  }
  const { data: counts } = useVoucherCounts(eventIdNum)
  const { data, isLoading, error } = useVouchers({
    event: eventIdNum,
    page,
    page_size: pageSize,
    search: search || undefined,
    status: status !== 'all' ? status : undefined,
  })

  const vouchers = data?.results || []
  const totalPages = data ? Math.ceil(data.count / pageSize) : 0
  const totalCount = data?.count || 0

  // Reset page when status changes
  useEffect(() => {
    setPage(1)
  }, [status, search])

  const tabs: { key: VoucherStatus; label: string; count: number; color: string }[] = [
    { key: 'all', label: 'All Vouchers', count: counts?.active || 0, color: 'blue' },
    { key: 'claimed', label: 'Claimed', count: counts?.claimed || 0, color: 'green' },
    { key: 'unclaimed', label: 'Unclaimed', count: counts?.unclaimed || 0, color: 'yellow' },
    { key: 'expired', label: 'Expired', count: counts?.expired || 0, color: 'red' },
  ]

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Header */}
        <div className="flex flex-row justify-between items-center mb-3 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Vouchers</h1>
          <div className="flex items-center space-x-3">
            {canBulkImportVouchers && (
              <button
                onClick={() => setShowBulkImportModal(true)}
                className="hidden sm:inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-xs sm:text-sm font-medium rounded-md shadow-sm text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <Upload className="-ml-1 mr-2 h-5 w-5" />
                Bulk Import
              </button>
            )}
            {canEditVouchers && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" />
                Create Voucher
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto">
          <div className="flex space-x-6 min-w-max items-center">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatus(tab.key)}
                className={cn(
                  'px-1 py-3 text-sm font-medium whitespace-nowrap flex items-center transition-colors',
                  status === tab.key
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-500'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                {tab.label}
                <span className={cn(
                  'ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full',
                  tab.color === 'blue' && 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
                  tab.color === 'green' && 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
                  tab.color === 'yellow' && 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400',
                  tab.color === 'red' && 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400'
                )}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-neutral-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by voucher name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md leading-5 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:placeholder-neutral-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vouchers Table */}
        <div className="flex-1">
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="bg-neutral-50 dark:bg-neutral-900 px-6 py-3">
                  <div className="flex space-x-4">
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4"></div>
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                  </div>
                </div>
                <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="px-6 py-4">
                      <div className="flex space-x-4">
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4"></div>
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/6"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <div className="text-red-600 dark:text-red-400 mb-2">
                  <svg className="mx-auto h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400">{error.message}</p>
              </div>
            ) : vouchers.length === 0 ? (
              <div className="px-6 py-8 text-center text-neutral-400 dark:text-neutral-500 text-sm">
                {status === 'all' && 'No vouchers found.'}
                {status === 'claimed' && 'No claimed vouchers found.'}
                {status === 'unclaimed' && 'No unclaimed vouchers found.'}
                {status === 'expired' && 'No expired vouchers found.'}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                    <thead className="bg-neutral-50 dark:bg-neutral-900">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Value</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Times Claimed</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Unique Users</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Max Uses</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Expiry</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Created</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                      {vouchers.map((voucher) => (
                        <tr key={voucher.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              {voucher.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-700 dark:text-neutral-300">
                              {voucher.type === 'items' ? (
                                <span className="inline-flex items-center">
                                  <Gift className="h-4 w-4 mr-1 text-purple-500" />
                                  {voucher.voucher_items_count || 0} item group{(voucher.voucher_items_count || 0) !== 1 ? 's' : ''}
                                </span>
                              ) : (
                                `${voucher.value} tokens`
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-700 dark:text-neutral-300">
                              {voucher.times_claimed || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-700 dark:text-neutral-300">
                              {getUniqueUsersDisplay(voucher)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-700 dark:text-neutral-300">
                              {voucher.max_uses ? (
                                `${voucher.max_uses} (${getRemainingUses(voucher, voucher.max_uses)} left)`
                              ) : (
                                'Unlimited'
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-700 dark:text-neutral-300">
                              {voucher.expiry ? formatDate(voucher.expiry, 'MMM d, yyyy h:mm a') : 'Never'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const displayStatus = getVoucherDisplayStatus(voucher)
                              return (
                                <>
                                  {displayStatus === 'expired' && (
                                    <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                                      Expired
                                    </span>
                                  )}
                                  {displayStatus === 'claimed' && (
                                    <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                      Claimed
                                    </span>
                                  )}
                                  {displayStatus === 'unclaimed' && (
                                    <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                                      Unclaimed
                                    </span>
                                  )}
                                  {voucher.is_available === false && (
                                    <span className="ml-1 px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400">
                                      Inactive
                                    </span>
                                  )}
                                </>
                              )
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-700 dark:text-neutral-300">
                              {formatDate(voucher.created)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                            <button
                              onClick={() => setSelectedVoucher(voucher)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 transition-colors duration-200"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white dark:bg-[#131313] px-4 py-3 flex items-center justify-between border-t border-neutral-200 dark:border-neutral-800 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      {page > 1 ? (
                        <button
                          onClick={() => setPage(p => p - 1)}
                          className="relative inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-700 text-sm font-medium rounded-md text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                        >
                          Previous
                        </button>
                      ) : <div />}
                      {page < totalPages ? (
                        <button
                          onClick={() => setPage(p => p + 1)}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-700 text-sm font-medium rounded-md text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                        >
                          Next
                        </button>
                      ) : <div />}
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-300">
                          Showing {vouchers.length} of {totalCount} vouchers
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-neutral-700 dark:text-neutral-300">Page</span>
                          {totalPages <= 10 ? (
                            <select
                              value={page}
                              onChange={(e) => setPage(Number(e.target.value))}
                              className="block w-20 px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {[...Array(totalPages)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="number"
                              value={page}
                              min={1}
                              max={totalPages}
                              onChange={(e) => setPage(Math.min(totalPages, Math.max(1, Number(e.target.value))))}
                              className="block w-16 px-2 py-1 text-sm text-center border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          )}
                          <span className="text-sm text-neutral-700 dark:text-neutral-300">of {totalPages}</span>
                        </div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className={cn(
                              'relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 dark:border-neutral-700 text-sm font-medium',
                              page === 1
                                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                                : 'bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                            )}
                          >
                            <span className="sr-only">Previous</span>
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className={cn(
                              'relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 dark:border-neutral-700 text-sm font-medium',
                              page === totalPages
                                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                                : 'bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                            )}
                          >
                            <span className="sr-only">Next</span>
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Voucher Modal */}
      {showCreateModal && (
        <CreateVoucherModal
          eventId={eventIdNum}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkImportModal
          eventId={eventIdNum}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}

      {/* Voucher Details Modal */}
      {selectedVoucher && (
        <VoucherDetailsModal
          voucher={selectedVoucher}
          eventId={eventIdNum}
          onClose={() => setSelectedVoucher(null)}
          canDelete={canDeleteVouchers}
          canEdit={canEditVouchers}
        />
      )}
    </div>
  )
}

// Create Voucher Modal Component
function CreateVoucherModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const createVoucher = useCreateVoucher()
  const { data: menuItemsData } = useMenuItems({ vendor_event__event: eventId, page_size: 500 })
  const menuItems = menuItemsData?.results || []
  
  const [formData, setFormData] = useState<CreateVoucherData>({
    name: '',
    type: 'token',
    value: 10,
    max_uses: 1,
    max_uses_per_user: 1,
    expiry_date: '',
    expiry_time: '23:59',
    is_available: true,
    hide_voucher_name: false,
  })
  const [itemGroups, setItemGroups] = useState<ItemGroup[]>([
    { quantity: 1, items: [], searchQuery: '' }
  ])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  // Item Groups functions
  const addItemGroup = () => {
    setItemGroups([...itemGroups, { quantity: 1, items: [], searchQuery: '' }])
  }

  const removeItemGroup = (index: number) => {
    if (itemGroups.length > 1) {
      setItemGroups(itemGroups.filter((_, i) => i !== index))
    }
  }

  const updateItemGroup = (index: number, updates: Partial<ItemGroup>) => {
    setItemGroups(itemGroups.map((group, i) => 
      i === index ? { ...group, ...updates } : group
    ))
  }

  const toggleItemInGroup = (groupIndex: number, itemId: number) => {
    const group = itemGroups[groupIndex]
    const idx = group.items.indexOf(itemId)
    if (idx > -1) {
      updateItemGroup(groupIndex, { items: group.items.filter(id => id !== itemId) })
    } else {
      updateItemGroup(groupIndex, { items: [...group.items, itemId] })
    }
  }

  const getFilteredItemsForGroup = (group: ItemGroup): MenuItem[] => {
    if (!group.searchQuery) return menuItems
    const query = group.searchQuery.toLowerCase()
    return menuItems.filter(item =>
      item.name.toLowerCase().includes(query) ||
      (item.vendor_name && item.vendor_name.toLowerCase().includes(query)) ||
      (item.location_name && item.location_name.toLowerCase().includes(query))
    )
  }

  const getDisplayLocation = (item: MenuItem): string => {
    // Try to get vendor name from flat fields first
    let vendorName = item.vendor_name || ''
    let locationName = item.location_name || ''
    
    // If not available, try to get from expanded vendor_event
    if (!vendorName && typeof item.vendor_event === 'object' && item.vendor_event) {
      const vendor = item.vendor_event.vendor
      if (typeof vendor === 'object' && vendor?.name) {
        vendorName = vendor.name
      }
    }
    if (!locationName && typeof item.vendor_event === 'object' && item.vendor_event?.booth) {
      locationName = item.vendor_event.booth
    }
    
    if (locationName && vendorName && locationName !== vendorName) {
      return locationName + ' - ' + vendorName
    }
    return locationName || vendorName || ''
  }

  const getVoucherItemsData = () => {
    return itemGroups
      .filter(group => group.items.length > 0)
      .map(group => ({
        quantity: group.quantity,
        items: group.items
      }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    try {
      // Build expiry datetime if date is provided
      let expiry: string | undefined
      if (formData.expiry_date) {
        expiry = `${formData.expiry_date}T${formData.expiry_time || '23:59'}:00`
      }

      const voucherData: Record<string, unknown> = {
        event: eventId,
        name: formData.name,
        type: formData.type,
        max_uses: formData.max_uses || undefined,
        max_uses_per_user: formData.max_uses_per_user || undefined,
        expiry,
        is_available: formData.is_available,
        hide_voucher_name: formData.hide_voucher_name,
      }

      if (formData.type === 'token') {
        voucherData.value = formData.value
      } else {
        voucherData.voucher_items_data = getVoucherItemsData()
      }

      await createVoucher.mutateAsync(voucherData as Parameters<typeof createVoucher.mutateAsync>[0])
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create voucher')
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Create New Voucher</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Voucher Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Voucher Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter voucher code (e.g., WELCOME10)"
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Users will enter this code to claim the voucher</p>
            </div>

            {/* Voucher Type */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Voucher Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as VoucherType })}
                className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="token">Token Voucher</option>
                <option value="items">Item Voucher</option>
              </select>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {formData.type === 'token' ? 'Tokens that can be spent on any item' : 'Apply to specific menu items'}
              </p>
            </div>

            {/* Token Value (shown only for token type) */}
            {formData.type === 'token' && (
              <div>
                <label htmlFor="value" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Token Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="value"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
                  min="1"
                  required
                  className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="10"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Number of tokens to give when claimed</p>
              </div>
            )}

            {/* Item Groups (shown only for items type) */}
            {formData.type === 'items' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Item Groups <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addItemGroup}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Group
                  </button>
                </div>
                
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Each group lets users pick a quantity from selected items</p>

                {/* Item Groups List */}
                {itemGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="border border-neutral-300 dark:border-neutral-700 rounded-lg p-4 space-y-3">
                    {/* Group Header */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        Group {groupIndex + 1}
                      </span>
                      {itemGroups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItemGroup(groupIndex)}
                          className="text-red-500 hover:text-red-600 text-xs"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                        Quantity to pick
                      </label>
                      <input
                        type="number"
                        value={group.quantity}
                        onChange={(e) => updateItemGroup(groupIndex, { quantity: Number(e.target.value) })}
                        min="1"
                        className="block w-24 px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">How many items user can pick from this group</p>
                    </div>

                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={group.searchQuery}
                        onChange={(e) => updateItemGroup(groupIndex, { searchQuery: e.target.value })}
                        placeholder="Search items..."
                        className="block w-full px-3 py-1.5 pl-8 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <Search className="absolute left-2.5 top-2 h-4 w-4 text-neutral-400" />
                    </div>

                    {/* Selected count */}
                    {group.items.length > 0 && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        <span className="font-medium text-blue-600">{group.items.length}</span> item(s) selected
                      </p>
                    )}

                    {/* Items List */}
                    <div className="border border-neutral-200 dark:border-neutral-700 rounded-md max-h-40 overflow-y-auto">
                      {getFilteredItemsForGroup(group).length === 0 ? (
                        <div className="p-3 text-center text-neutral-500 dark:text-neutral-400 text-xs">
                          {menuItems.length === 0 ? 'No menu items available' : 'No items match your search'}
                        </div>
                      ) : (
                        getFilteredItemsForGroup(group).map(item => (
                          <label
                            key={item.id}
                            className="flex items-center px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer border-b border-neutral-100 dark:border-neutral-700 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={group.items.includes(item.id)}
                              onChange={() => toggleItemInGroup(groupIndex, item.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                            />
                            <div className="ml-3 flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{item.name}</span>
                                <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-2 flex-shrink-0">{item.price} tokens</span>
                              </div>
                              <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate block">{getDisplayLocation(item)}</span>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="max_uses" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Max Total Uses
                </label>
                <input
                  type="number"
                  id="max_uses"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? Number(e.target.value) : '' })}
                  min="1"
                  className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Unlimited"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Leave empty for unlimited</p>
              </div>

              <div>
                <label htmlFor="max_uses_per_user" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Max Uses Per User
                </label>
                <input
                  type="number"
                  id="max_uses_per_user"
                  value={formData.max_uses_per_user}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value ? Number(e.target.value) : '' })}
                  min="1"
                  className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Max times each user can claim</p>
              </div>
            </div>

            {/* Expiry */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiry_date" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expiry Date
                </label>
                <input
                  type="date"
                  id="expiry_date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="expiry_time" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expiry Time
                </label>
                <input
                  type="time"
                  id="expiry_time"
                  value={formData.expiry_time}
                  onChange={(e) => setFormData({ ...formData, expiry_time: e.target.value })}
                  className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md shadow-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Is Available */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_available"
                checked={formData.is_available}
                onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-700 rounded"
              />
              <label htmlFor="is_available" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                Voucher is active and can be claimed
              </label>
            </div>

            {/* Hide Voucher Name */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="hide_voucher_name"
                checked={formData.hide_voucher_name}
                onChange={(e) => setFormData({ ...formData, hide_voucher_name: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-700 rounded"
              />
              <label htmlFor="hide_voucher_name" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                Hide Voucher Name from Users
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createVoucher.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {createVoucher.isPending ? 'Creating...' : 'Create Voucher'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Bulk Import Modal Component
function BulkImportModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const bulkImport = useBulkImportVouchers()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragover, setDragover] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragover(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      setSelectedFile(files[0])
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) return
    setError(null)

    try {
      await bulkImport.mutateAsync({ eventId, file: selectedFile })
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to import vouchers')
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Bulk Import Vouchers</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Instructions */}
          <div className="mb-6">
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">
              Upload a CSV file to import multiple vouchers at once. The CSV should have the following columns:
            </p>
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md p-3 text-xs font-mono text-neutral-700 dark:text-neutral-300">
              name,value,max_uses,max_uses_per_user,expiry
            </div>
            <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>name</strong> (required): Voucher code</li>
                <li><strong>value</strong> (required): Token value</li>
                <li><strong>max_uses</strong> (optional): Total claims allowed (default: 1)</li>
                <li><strong>max_uses_per_user</strong> (optional): Claims per user (default: 1)</li>
                <li><strong>expiry</strong> (optional): Expiry date (YYYY-MM-DD format)</li>
              </ul>
            </div>
          </div>

          {/* Download Template Link */}
          <div className="mb-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                // Generate and download template
                const csv = 'name,value,max_uses,max_uses_per_user,expiry\nWELCOME10,10,1,1,2025-12-31'
                const blob = new Blob([csv], { type: 'text/csv' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'voucher_import_template.csv'
                a.click()
                URL.revokeObjectURL(url)
              }}
              className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download CSV Template
            </a>
          </div>

          {/* Upload Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Drop Zone */}
            <div className="relative">
              <label
                htmlFor="csv_file"
                className="block cursor-pointer"
                onDragOver={(e) => { e.preventDefault(); setDragover(true) }}
                onDragLeave={(e) => { e.preventDefault(); setDragover(false) }}
                onDrop={handleDrop}
              >
                <div className={cn(
                  'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
                  dragover
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-neutral-300 dark:border-neutral-700'
                )}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <span className="font-medium text-blue-600 dark:text-blue-400">Click to upload</span> or drag and drop
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">CSV files only</p>
                  <p className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {selectedFile ? selectedFile.name : 'No file selected'}
                  </p>
                </div>
              </label>
              <input
                type="file"
                id="csv_file"
                ref={fileInputRef}
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="sr-only"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedFile || bulkImport.isPending}
                className={cn(
                  'inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors',
                  (!selectedFile || bulkImport.isPending) && 'opacity-50 cursor-not-allowed'
                )}
              >
                {bulkImport.isPending && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {bulkImport.isPending ? 'Importing...' : 'Import Vouchers'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Voucher Details Modal Component
interface VoucherClaim {
  id: number
  user: number
  user_email: string
  user_name: string
  claimed_at: string
}

function VoucherDetailsModal({
  voucher,
  eventId,
  onClose,
  canDelete,
  canEdit,
}: {
  voucher: Voucher
  eventId: number
  onClose: () => void
  canDelete: boolean
  canEdit: boolean
}) {
  const deleteVoucher = useDeleteVoucher()
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [claims, setClaims] = useState<VoucherClaim[]>([])
  const [totalClaims, setTotalClaims] = useState(0)
  const [nextClaimsUrl, setNextClaimsUrl] = useState<string | null>(null)
  const [loadingClaims, setLoadingClaims] = useState(false)
  const [loadingMoreClaims, setLoadingMoreClaims] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [claimsVersion, setClaimsVersion] = useState(0)

  const CLAIMS_PAGE_SIZE = 50

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showAssignModal) {
          setShowAssignModal(false)
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, showAssignModal])

  // Fetch the first page of claims from the paginated claims endpoint
  useEffect(() => {
    const fetchClaims = async () => {
      setLoadingClaims(true)
      try {
        const token = localStorage.getItem('eventpay_token')
        if (!token) return

        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.eventpay.com/en/api'
        const response = await fetch(
          `${apiBaseUrl}/token-voucher/${voucher.id}/claims/?limit=${CLAIMS_PAGE_SIZE}&offset=0`,
          {
            headers: {
              'Authorization': `Token ${token}`,
              'Accept': 'application/json',
            },
          },
        )

        if (response.ok) {
          const data = await response.json()
          setClaims(data.results || [])
          setTotalClaims(data.count || 0)
          setNextClaimsUrl(data.next || null)
        }
      } catch (err) {
        console.error('Error fetching claims:', err)
      } finally {
        setLoadingClaims(false)
      }
    }

    fetchClaims()
  }, [voucher.id, claimsVersion])

  const loadMoreClaims = async () => {
    if (!nextClaimsUrl || loadingMoreClaims) return
    setLoadingMoreClaims(true)
    try {
      const token = localStorage.getItem('eventpay_token')
      if (!token) return

      const response = await fetch(nextClaimsUrl, {
        headers: {
          'Authorization': `Token ${token}`,
          'Accept': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setClaims((prev) => [...prev, ...(data.results || [])])
        setNextClaimsUrl(data.next || null)
      }
    } catch (err) {
      console.error('Error loading more claims:', err)
    } finally {
      setLoadingMoreClaims(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await deleteVoucher.mutateAsync(voucher.id)
      onClose()
    } catch (err) {
      console.error('Error deleting voucher:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const displayStatus = getVoucherDisplayStatus(voucher)
  const isActive = voucher.is_available !== false && displayStatus !== 'expired'

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
            Voucher Details: <span className="text-blue-600 dark:text-blue-400">{voucher.name}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Left Column - Voucher Info */}
            <div>
              <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                {voucher.name.toUpperCase()}
              </h3>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Type:</dt>
                  <dd>
                    <span className={cn(
                      'px-2 py-0.5 text-xs font-medium rounded',
                      voucher.type === 'items' 
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                    )}>
                      {voucher.type === 'items' ? 'Item Voucher' : 'Token Voucher'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Value:</dt>
                  <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {voucher.type === 'items' 
                      ? `${voucher.voucher_items_count || 0} item group${(voucher.voucher_items_count || 0) !== 1 ? 's' : ''}`
                      : `${voucher.value} tokens`
                    }
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Times Claimed:</dt>
                  <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {voucher.times_claimed || 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Unique Users:</dt>
                  <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {getUniqueUsersDisplay(voucher)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Right Column - Usage Limits */}
            <div>
              <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                USAGE LIMITS
              </h3>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Max Uses:</dt>
                  <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {voucher.max_uses || 'Unlimited'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Max Uses Per User:</dt>
                  <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {voucher.max_uses_per_user || 1}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-neutral-600 dark:text-neutral-400">Hide Voucher Name:</dt>
                  <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {voucher.hide_voucher_name ? 'Enabled' : 'Disabled'}
                  </dd>
                </div>
              </dl>
              {canEdit && isActive && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Assign to User
                </button>
              )}
            </div>
          </div>

          {/* Status & Dates */}
          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-6 mb-6">
            <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
              STATUS & DATES
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <dt className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Created:</dt>
                <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {formatDate(voucher.created, 'MMM d, yyyy h:mm a')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Created By:</dt>
                <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {voucher.created_by_name 
                    || (voucher.created_by ? `${voucher.created_by.first_name} ${voucher.created_by.last_name}` : null)
                    || 'Unknown'
                  }
                </dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Expires:</dt>
                <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {voucher.expiry ? formatDate(voucher.expiry, 'MMM d, yyyy h:mm a') : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Status:</dt>
                <dd>
                  <span className={cn(
                    'text-sm font-medium',
                    isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  )}>
                    {isActive ? 'Active' : displayStatus === 'expired' ? 'Expired' : 'Inactive'}
                  </span>
                </dd>
              </div>
            </div>
          </div>

          {/* Claims Section */}
          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-6">
            {loadingClaims ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : claims.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-3" />
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">No claims yet</p>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  This voucher hasn't been claimed by any users.
                </p>
              </div>
            ) : (
              <div>
                <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                  CLAIMS ({totalClaims})
                  {claims.length < totalClaims && (
                    <span className="ml-2 text-neutral-400 dark:text-neutral-500 normal-case font-normal">
                      showing {claims.length} of {totalClaims}
                    </span>
                  )}
                </h3>
                <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                    <thead className="bg-neutral-50 dark:bg-neutral-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">User</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Claimed At</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Tokens</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                      {claims.map((claim) => (
                        <tr key={claim.id}>
                          <td className="px-4 py-2">
                            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              {claim.user_name}
                            </div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">
                              {claim.user_email}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300">
                            {formatDate(claim.claimed_at, 'MMM d, yyyy h:mm a')}
                          </td>
                          <td className="px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300">
                            {voucher.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {nextClaimsUrl && (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={loadMoreClaims}
                      disabled={loadingMoreClaims}
                      className="px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMoreClaims ? 'Loading…' : `Load more (${totalClaims - claims.length} remaining)`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-between items-center p-6 border-t border-neutral-200 dark:border-neutral-800">
          <div>
            {canDelete && !showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!isActive}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md transition-colors",
                  isActive 
                    ? "text-white bg-red-600 hover:bg-red-700" 
                    : "text-neutral-400 bg-neutral-200 dark:bg-neutral-700 cursor-not-allowed"
                )}
                title={!isActive ? "Cannot delete inactive vouchers" : undefined}
              >
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-red-600 dark:text-red-400">Are you sure?</span>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Assign Voucher Modal */}
      {showAssignModal && (
        <AssignVoucherModal
          voucher={voucher}
          eventId={eventId}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => setClaimsVersion((v) => v + 1)}
        />
      )}
    </div>
  )
}

// Assign Voucher Modal — search for a user and assign the voucher to them
function AssignVoucherModal({
  voucher,
  eventId,
  onClose,
  onAssigned,
}: {
  voucher: Voucher
  eventId: number
  onClose: () => void
  onAssigned: () => void
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 300)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const assignVoucher = useAssignVoucher()

  const { data: usersData, isLoading: isSearching } = useEventUsers(
    eventId,
    { search: debouncedSearch, page_size: 10 },
    { enabled: debouncedSearch.length >= 2 },
  )

  const users = usersData?.results || []

  // Auto-focus search input on open
  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  const handleAssign = async () => {
    if (!selectedUser) return
    setAssignError(null)
    setAssignSuccess(null)

    try {
      const result = await assignVoucher.mutateAsync({
        voucherId: voucher.id,
        userId: selectedUser.id,
      })
      setAssignSuccess(result.message || 'Voucher assigned successfully')
      setSelectedUser(null)
      setSearchQuery('')
      onAssigned()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to assign voucher'
      setAssignError(message)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Assign Voucher: <span className="text-blue-600 dark:text-blue-400">{voucher.name}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 flex-1 overflow-y-auto">
          {/* Success message */}
          {assignSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-md">
              <p className="text-sm text-green-700 dark:text-green-400">{assignSuccess}</p>
            </div>
          )}

          {/* Error message */}
          {assignError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-md">
              <p className="text-sm text-red-700 dark:text-red-400">{assignError}</p>
            </div>
          )}

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setSelectedUser(null)
                setAssignError(null)
              }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>

          {/* Selected user */}
          {selectedUser && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-md flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {selectedUser.first_name} {selectedUser.last_name}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {[selectedUser.email, selectedUser.phone_number || selectedUser.phone].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Search results */}
          {!selectedUser && debouncedSearch.length >= 2 && (
            <div className="mt-2 border border-neutral-200 dark:border-neutral-700 rounded-md overflow-hidden">
              {isSearching ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              ) : users.length === 0 ? (
                <div className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  No users found
                </div>
              ) : (
                <ul className="divide-y divide-neutral-200 dark:divide-neutral-700 max-h-60 overflow-y-auto">
                  {users.map((user) => (
                    <li key={user.id}>
                      <button
                        onClick={() => {
                          setSelectedUser(user)
                          setAssignError(null)
                          setAssignSuccess(null)
                        }}
                        className="w-full px-3 py-2.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                      >
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          {[user.email, user.phone_number || user.phone].filter(Boolean).join(' · ')}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Hint when no search */}
          {!selectedUser && debouncedSearch.length < 2 && !assignSuccess && (
            <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500">
              Type at least 2 characters to search for users
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedUser || assignVoucher.isPending}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors',
              selectedUser && !assignVoucher.isPending
                ? 'text-white bg-blue-600 hover:bg-blue-700'
                : 'text-neutral-400 bg-neutral-200 dark:bg-neutral-700 cursor-not-allowed',
            )}
          >
            {assignVoucher.isPending ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  )
}
